import {
  createClient,
  type RealtimeClientOptions,
  type SupabaseClientOptions,
} from '@supabase/supabase-js';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

import type { Database } from '@sixam/shared/database.types';
import { resolveLocalUrl } from '@sixam/shared/local-url';

const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing EXPO_PUBLIC_SUPABASE_URL or EXPO_PUBLIC_SUPABASE_ANON_KEY. Set them in apps/mobile/.env.local for local Expo development.'
  );
}

// Persist the session in the device keychain/keystore on native platforms.
// On web, supabase-js falls back to localStorage in the browser (and memory during SSR).
const SecureStoreAdapter = {
  getItem: (key: string) => SecureStore.getItemAsync(key),
  setItem: (key: string, value: string) => SecureStore.setItemAsync(key, value),
  removeItem: (key: string) => SecureStore.deleteItemAsync(key),
};

const isWebServerRender = Platform.OS === 'web' && typeof window === 'undefined';

class ServerRenderWebSocket {
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSING = 2;
  static readonly CLOSED = 3;

  readonly CONNECTING = ServerRenderWebSocket.CONNECTING;
  readonly OPEN = ServerRenderWebSocket.OPEN;
  readonly CLOSING = ServerRenderWebSocket.CLOSING;
  readonly CLOSED = ServerRenderWebSocket.CLOSED;
  readonly readyState = ServerRenderWebSocket.CLOSED;
  readonly protocol = '';
  readonly url: string;
  onopen = null;
  onmessage = null;
  onclose = null;
  onerror = null;

  constructor(address: string | URL) {
    this.url = String(address);
  }

  close() {}
  send() {}
  addEventListener() {}
  removeEventListener() {}
}

const serverRenderTransport = ServerRenderWebSocket as unknown as NonNullable<
  RealtimeClientOptions['transport']
>;

const realtimeOptions: SupabaseClientOptions<'public'>['realtime'] = isWebServerRender
  ? { transport: serverRenderTransport }
  : undefined;

const supabaseOptions: SupabaseClientOptions<'public'> = {
  auth: {
    ...(Platform.OS === 'web' ? {} : { storage: SecureStoreAdapter }),
    autoRefreshToken: true,
    flowType: 'pkce',
    persistSession: true,
    detectSessionInUrl: Platform.OS === 'web',
  },
  ...(realtimeOptions ? { realtime: realtimeOptions } : {}),
};

export const supabase = createClient<Database>(
  resolveLocalUrl(supabaseUrl, 'supabase-client-url'),
  supabaseAnonKey,
  supabaseOptions
);
